import axios from "@/axios";

class AuthService {
  async login(user) {
    try {
      const response = await axios.post("login", {
        email: user.email,
        password: user.password,
      });

      if (response.data.token) {
        localStorage.setItem("user", JSON.stringify(response.data));
      }
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async logout(user) {
    try {
      const payload = {};
      if (user.sessionId) {
        payload.sessionId = user.sessionId;
      }
      if (localStorage.getItem("sso")) {
        payload.ssoLogin = true;
      }
      const response = await axios.post(`logout/${user.user.id}`, payload);
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Consume a signed impersonation URL generated by the admin panel.
   *
   * security/0.3: previously this posted a raw Passport token id (from the URL
   * path) to /verify-token. The API would then mint a new access token from
   * possession of that id — a URL leak (referer, history, logs) was full
   * account takeover. The admin panel now redirects to
   * `FRONTEND_URL/verify-token?intent=<url-encoded signed URL>`. We POST to
   * the full signed URL exactly as generated; Laravel's `signed` middleware
   * validates the HMAC + expiry server-side, and the controller enforces
   * single-use via a cache-based nonce.
   */
  async loginAsUser(signedUrl) {
    try {
      const response = await axios.post(signedUrl, {});
      if (response.data.token) {
        localStorage.setItem("user", JSON.stringify(response.data));
      }
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async register(user) {
    try {
      const response = await axios.post("signup", {
        email: user.email,
        firstname: user.firstname,
        lastname: user.lastname,
        password: user.password,
        password_confirmation: user.password_confirmation,
        title: user.title,
        entity_id: user.entity_id,
        organization: user.organization,
        city: user.city,
        postal: user.postal,
        country: user.country,
        state: user.state,
        aff: user.aff,
        serial: user.serial,
        user_case: user.user_case,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async forgot(user) {
    try {
      const response = await axios.post("forgot", { email: user.email });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async permissions(user) {
    try {
      let entityClassId = user.user.entity
        ? user.user.entity.entity_class_id
        : null;

      const response = await axios.post(`permissions/${user.user.id}`, {
        entityClassId: entityClassId,
      });
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async checkToken() {
    try {
      const response = await axios.get("check-token");
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  async siteSettings() {
    try {
      const response = await axios.get("site-settings");
      return response.data;
    } catch (error) {
      this.handleError(error);
    }
  }

  // Centralized error handler
  handleError(error) {
    let message = "An unexpected error occurred";

    if (error.response) {
      // Server responded with a status outside 2xx
      message =
        error.response.data.message || `Error: ${error.response.status}`;
    } else if (error.request) {
      // Request was made but no response received
      message = "No response from the server. Please try again.";
    } else {
      // Something else happened
      message = error.message;
    }
    throw new Error(message); // re-throw for Vuex actions or components to catch
  }
}

export default new AuthService();
